import React, { useEffect, useState } from "react";
import BackButton from "../GoBack/GoBack";
import { useAuthStudent } from "../../../context/StudentLoginContext/StudentAuthContext";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./Quests.css";
import { StudentMapMenu } from "../Map/MapMenu/MapMenu";
import { useGetQuestsList } from "../../../../hooks/student/quest/useGetQuestsList";
import { useTranslation } from "react-i18next";
import { loadStudentQuestI18n } from "../../../i18n/loaders/student.i18n";
import { useI18nLoader } from "../../../i18n/loaders/useI18nLoader";

/**
 * Individual Quest / Festival Item with smooth image-level skeleton loading.
 * Shows skeleton until thumbnail is provided and loaded from the backend API.
 * If backend thumbnail is null or fails to load, gracefully displays branded quest badge.
 */
const QuestItem = ({ item, linkTo }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const thumbnailUrl = item?.thumbnail_url;
  const showSkeleton = thumbnailUrl && !imageLoaded && !imageError;
  const showImage = thumbnailUrl && imageLoaded && !imageError;
  const showFallback = !thumbnailUrl || imageError;

  return (
    <div className="col-6 col-md-4 col-lg-3 text-center mb-4">
      <div className="quest-item-wrapper">
        <Link to={linkTo} className="text-decoration-none">
          <div className="quest-circle-container">
            {/* Skeleton shimmer while backend image downloads */}
            {showSkeleton && (
              <div className="quest-skeleton-overlay">
                <Skeleton
                  circle
                  width="100%"
                  height="100%"
                  baseColor="#E0E0E0"
                  highlightColor="#F5F5F5"
                  duration={1.5}
                  className="quest-skeleton-circle"
                />
              </div>
            )}

            {/* Real backend image */}
            {thumbnailUrl && !imageError && (
              <img
                src={thumbnailUrl}
                alt={item?.title || item?.label || "Quest"}
                className={`questthumbnail_url ${imageLoaded ? "loaded" : "loading"}`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            )}

            {/* Graceful fallback when thumbnail_url is null or image fails to load */}
            {showFallback && (
              <div className="quest-thumbnail-fallback d-flex flex-column justify-content-center align-items-center text-white px-2">
                <span className="quest-fallback-title fw-bold text-center">
                  {item?.title || item?.label || "Quest"}
                </span>
              </div>
            )}

            {/* Play video overlay icon */}
            {(showImage || showFallback) && (
              <img
                src="https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/quest/icon/PlayVideo.png"
                alt="play"
                className="play-icon-logo"
              />
            )}
          </div>
        </Link>
      </div>
    </div>
  );
};

export const StudentAdminQuests = () => {
  const tabs = [
    { key: "quest", value: "quest" },
    { key: "festive", value: "festive" },
  ];
  const { t } = useTranslation("student-quest");
  const i18nReady = useI18nLoader(loadStudentQuestI18n);
  const navigate = useNavigate();
  const { userType } = useOutletContext();
  const { student, fetchWithAuthStudent } = useAuthStudent(); // Fetch student details from AuthContext
  const apiUrl = process.env.REACT_APP_API_URL;
  const [quests, setQuests] = useState([]);
  const { quests: studentQuestsList, getStudentQuests } = useGetQuestsList();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("quest");

  const filteredQuests = quests?.filter(
    (item) => activeTab.toLowerCase() === item.type && item.status === "1",
  );

  const getTabStyle = (tab) => ({
    backgroundColor: activeTab === tab ? "#0037AC" : "#ffffff",
    color: activeTab === tab ? "#ffffff" : "#0037AC",
    cursor: "pointer",
    transition: "all 0.3s",
  });

  const handleNavigateToMap = () => {
    if (activeTab === "quest") {
      navigate("/student/start-your-quest");
    } else {
      navigate("/student/festival");
    }
  };

  useEffect(() => {
    if (userType === "school-student") {
      const fetchStudentQuests = async () => {
        try {
          setLoading(true);
          await getStudentQuests(activeTab.toLowerCase()); // ✔ dynamic fetch
        } catch (err) {
          console.error("Error fetching student quests list:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchStudentQuests();
    }
  }, [userType, activeTab]);

  useEffect(() => {
    const fetchQuestData = async () => {
      if (!student || !student.token) {
        return;
      }

      try {
        setLoading(true);
        const response = await fetchWithAuthStudent(
          `${apiUrl}/student/quests`,
          {
            method: "GET",
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch quests");
        }

        const data = await response.json();
        setQuests(data);
      } catch (error) {
        console.error("Error fetching quests:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestData();
  }, [apiUrl, student, fetchWithAuthStudent]);

  if (!i18nReady) return null; // or loader

  const displayList =
    userType === "school-student" ? studentQuestsList : filteredQuests;

  return (
    <div className="StudentAdminBody StudentBodyMrgn">
      {/* <img className="circleTopBar" src={TopCircle} alt="" /> */}
      <div className="container-fluid">
        <div className="teacher-bodycontent mb-4">
          <section>
            <div className="row mb-2 mb-md-4">
              <div className="col-12">
                <BackButton />
              </div>
            </div>

            <div className="row justify-content-center">
              <div className="col-12 col-xl-10">
                {/* Centered Banner with Responsive Sizing */}
                <div className="quest-banner-container">
                  {/* Desktop Image */}
                  <img
                    src="https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/common/BlueBanner.png"
                    alt="quest"
                    className="img-fluid w-100 d-none d-md-block quest-banner-img-desktop"
                    style={{ objectFit: "cover", height: "auto" }}
                  />

                  {/* Mobile Image */}
                  <img
                    src="https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/common/BlueBannerMobile.png"
                    alt="quest"
                    className="img-fluid w-100 d-block d-md-none quest-banner-img-mobile"
                  />

                  {/* Centered Text */}
                  <div className="quest-banner-text-overlay position-absolute top-50 start-50 translate-middle text-white text-center">
                    <h6 className="quest-banner-title fw-semibold m-0">
                      {t("quest.list.title", {
                        type: t(`quest.types.${activeTab}`),
                      })}
                    </h6>
                    <p className="quest-banner-subtitle m-0">
                      {t("quest.list.subtitle")}
                    </p>
                  </div>
                </div>

                {/* Quest / Festival Selection Tabs */}
                <div className="quest-tabs-container">
                  <div className="row justify-content-center g-2 g-md-4">
                    {tabs.map((tab) => (
                      <div key={tab.value} className="col-6 col-md-5 col-lg-4">
                        <div
                          className="quest-tab-card shadow-sm border text-center h-100 d-flex flex-column justify-content-center"
                          style={getTabStyle(tab.value)}
                          onClick={() => setActiveTab(tab.value)}
                        >
                          <h5 className="quest-tab-title fw-bold text-uppercase mb-0">
                            {t(`quest.types.${tab.key}`)}
                          </h5>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* View Mode Toggle: List View vs Map View */}
                  <div className="view-mode-container">
                    <div className="view-mode-pill">
                      <button
                        className="view-mode-btn active"
                        type="button"
                        title={t("quest.viewMode.listView", "List View")}
                      >
                        <i className="fa-solid fa-table-cells-large"></i>
                        <span>{t("quest.viewMode.listView", "List View")}</span>
                      </button>
                      <button
                        className="view-mode-btn"
                        type="button"
                        onClick={handleNavigateToMap}
                        title={
                          activeTab === "quest"
                            ? t("quest.viewMode.viewQuestMap", "View Quest Map")
                            : t(
                                "quest.viewMode.viewFestivalMap",
                                "View Festival Map",
                              )
                        }
                      >
                        <i className="fa-solid fa-map-location-dot"></i>
                        <span>
                          {activeTab === "quest"
                            ? t("quest.viewMode.viewQuestMap", "Quest Map")
                            : t(
                                "quest.viewMode.viewFestivalMap",
                                "Festival Map",
                              )}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Content Area: Skeleton Loader or Loaded Items */}
                <SkeletonTheme baseColor="#E0E0E0" highlightColor="#F5F5F5">
                  {loading ? (
                    <div className="row quest-grid-row justify-content-center">
                      {Array.from({ length: 8 }).map((_, index) => (
                        <div
                          key={index}
                          className="col-6 col-md-4 col-lg-3 text-center mb-4"
                        >
                          <div className="quest-circle-container">
                            <Skeleton
                              circle
                              width="100%"
                              height="100%"
                              baseColor="#E0E0E0"
                              highlightColor="#F5F5F5"
                              duration={1.5}
                              className="quest-skeleton-circle"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="row quest-grid-row justify-content-center">
                      {displayList?.map((item, index) => {
                        const linkTo =
                          item?.type === "quest"
                            ? `/student/admin/episodes/${item?.id}`
                            : `/student/admin/episodes/festive/detail/${item?.id}/${item?.nextChapterId?.[0]}`;

                        return (
                          <QuestItem
                            key={item?.id || index}
                            item={item}
                            linkTo={linkTo}
                          />
                        );
                      })}
                    </div>
                  )}
                </SkeletonTheme>
              </div>
            </div>
          </section>
          <div>
            {/* Left Image */}
            <img
              src="https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/common/LeftSideDesign.png"
              alt="left"
              className="position-fixed d-none d-lg-block"
              style={{
                left: 0,
                top: "10vh",
                height: "90vh",
                bottom: "auto",
                width: "auto",
                zIndex: -1, // Keeps it in the background
              }}
            />

            {/* Right Image */}
            <img
              src="https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/common/RightSideDesign.png"
              alt="right"
              className="position-fixed d-none d-lg-block"
              style={{
                right: 0,
                top: "20vh",
                height: "70vh",
                width: "auto",
                zIndex: -1, // Keeps it in the background
              }}
            />

            {/* Page Content */}
          </div>
          {/* <StudentFooter /> */}
          <StudentMapMenu />
        </div>
      </div>
    </div>
  );
};
